'use client';

import Link from "next/link";
import Image from "next/image";
import { NewsletterForm } from "@/components/newsletter-form";
import { Button } from "./ui/button";
import { useParams } from 'next/navigation';

export function Footer() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  return (
    <div className="px-4 pb-4 mt-24">
      <footer className="footer-gradient-border w-full">
        <div className="container py-16">
          <div className="text-center mb-12">
            <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-3">GET STARTED</h2>
            <h3 className="text-4xl font-bold mb-4">Ready to build something extraordinary?<br />Partner with OpenLabs Research.</h3>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
            We design cutting-edge AI and software solutions tailored to your goals. Let’s bring your next innovation to life.
            </p>
            <Button 
              size="pill" 
              variant="default" 
              className="rounded-full px-8 py-6 h-auto text-base font-medium"
              asChild
            >
              <Link href={`/${currentLocale}/contact`}>Contact Us</Link>
            </Button>
          </div>
          
          <hr className="border-t border-border/30 my-8" />
          
          {/* Footer Navigation */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 py-8">
            <div>
              <Link href={`/${currentLocale}`} className="flex items-center gap-2 font-bold text-xl mb-6">
                <Image 
                  src="/openlabslogo.png" 
                  alt="OpenLabs Logo" 
                  width={32} 
                  height={32} 
                  className="rounded-full"
                />
                OpenLabs Research
              </Link>
            </div>
            
            <div>
              <h3 className="font-medium text-lg mb-4">Product</h3>
              <ul className="space-y-3">
                <li><Link href={`/${currentLocale}/services`} className="text-muted-foreground hover:text-foreground transition">Services</Link></li>
                <li><Link href={`/${currentLocale}/portfolio`} className="text-muted-foreground hover:text-foreground transition">Portfolio</Link></li>
                {/* <li><Link href="/ai-demos" className="text-muted-foreground hover:text-foreground transition">AI Demos</Link></li> */}
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-lg mb-4">Company</h3>
              <ul className="space-y-3">
                <li><Link href={`/${currentLocale}/blog`} className="text-muted-foreground hover:text-foreground transition">Blog</Link></li>
                <li><Link href={`/${currentLocale}/contact`} className="text-muted-foreground hover:text-foreground transition">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-lg mb-4">Support</h3>
              <ul className="space-y-3">
                <li><Link href={`/${currentLocale}/privacy-policy`} className="text-muted-foreground hover:text-foreground transition">Privacy Policy</Link></li>
                <li><Link href={`/${currentLocale}/terms`} className="text-muted-foreground hover:text-foreground transition">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <hr className="border-t border-border/30 my-6" />
          
          {/* Bottom section with copyright and social links */}
          <div className="flex flex-col md:flex-row justify-between items-center py-6">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} OpenLabs Research. All rights reserved.
            </p>
            
            <div className="flex space-x-5 mt-4 md:mt-0">
              <Link href="https://x.com/openlabsr?s=21" target="_blank" className="text-muted-foreground hover:text-purple-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="sr-only">Twitter X</span>
              </Link>
              <Link href="https://www.linkedin.com/company/openlabsresearch/?lipi=urn%3Ali%3Apage%3Ad_flagship3_search_srp_companies%3BEpxN30c3RnO6Dnycgbv8vQ%3D%3D" target="_blank" className="text-muted-foreground hover:text-purple-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
                </svg>
                <span className="sr-only">LinkedIn</span>
              </Link>
              <Link href="https://www.instagram.com/openlabsresearch?igsh=MThydWg3bW81OWNiZQ%3D%3D&utm_source=qr" target="_blank" className="text-muted-foreground hover:text-purple-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M16.98 0a6.9 6.9 0 0 1 5.08 1.98A6.94 6.94 0 0 1 24 7.02v9.96c0 2.08-.68 3.87-1.98 5.13A7.14 7.14 0 0 1 16.94 24H7.06a7.06 7.06 0 0 1-5.03-1.89A6.96 6.96 0 0 1 0 16.94V7.02C0 2.8 2.8 0 7.02 0h9.96zm.05 2.23H7.06c-1.45 0-2.7.43-3.53 1.25a4.82 4.82 0 0 0-1.3 3.54v9.92c0 1.5.43 2.7 1.3 3.58a5 5 0 0 0 3.53 1.25h9.88a5 5 0 0 0 3.53-1.25 4.73 4.73 0 0 0 1.4-3.54V7.02a5 5 0 0 0-1.3-3.49 4.82 4.82 0 0 0-3.54-1.3zM12 5.76c3.39 0 6.2 2.8 6.2 6.2a6.2 6.2 0 0 1-12.4 0 6.2 6.2 0 0 1 6.2-6.2zm0 2.22a3.99 3.99 0 0 0-3.97 3.97A3.99 3.99 0 0 0 12 15.92a3.99 3.99 0 0 0 3.97-3.97A3.99 3.99 0 0 0 12 7.98zm6.44-3.77a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z"/>
                </svg>
                <span className="sr-only">Instagram</span>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}